import React from 'react';
import {
    BlankModal,
    Flex,
    HStack,
    ModalBody,
    ModalFooter,
    ModalHeader,
    VStack,
    Spinner,
    Text,
    Box,
} from '@cad-arc';
import { useFullscreenModalControls } from '../../modals/context/modal-context-provider';
import { DispatchAreaObject, ManageDispatchAreasResource } from '@mark43/cad-api';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useApiClient } from '~/api-client';
import { DispatchAreaManagementForm } from './types';
import { DispatchFor } from './manage-dispatch-areas-dispatch-for';
import { QuickGrabSection } from './manage-dispatch-areas-quick-grab';
import { SelectionTables } from './manage-dispatch-areas-selection-tables';
import { IntegratedFormProvider } from '../../forms/integrated-form-provider';
import { FormBody } from '../../forms/integrated-form-components/form-body';
import { FormState } from '../../forms/integrated-form-components/form-state';
import { FormCancelButton } from '../../forms/integrated-form-components/form-cancel-button';
import { FormSaveButton } from '../../forms/integrated-form-components/form-save-button';

export type ManageDispatchAreasModalType = typeof ManageDispatchAreasModal;

export const ManageDispatchAreasModal: React.FC = () => {
    const modalControls = useFullscreenModalControls();
    const close = React.useCallback(() => {
        modalControls.close();
    }, [modalControls]);

    const apiClient = useApiClient();

    const query = useQuery(['dispatch-area-state'], () => {
        return apiClient
            .request<ManageDispatchAreasResource.GetManageDispatchAreasView>({
                method: 'POST',
                url: 'manage_dispatch_areas',
                data: { includeAllObjects: true },
            })
            .then((data) => {
                if (data.data.data) {
                    return data.data.data;
                }
            });
    });

    const { data: manageDispatchAreasView } = query;

    const allDispatchAreaObjects = manageDispatchAreasView?.allDispatchAreaObjects;
    const allDispatchAreaGroupObjects = manageDispatchAreasView?.allDispatchAreaGroupObjects;
    const userDispatchAreaObjects = manageDispatchAreasView?.userDispatchAreaObjects;

    const form = useForm<DispatchAreaManagementForm>({
        defaultValues: {},
    });

    const mutation = useMutation(
        (form: DispatchAreaManagementForm) => {
            const allDispatchAreaObjectsByDispatchAreaId = Object.fromEntries(
                allDispatchAreaObjects?.map((dispatchAreaObject) => [
                    dispatchAreaObject.id,
                    dispatchAreaObject,
                ]) ?? []
            );
            const dispatchAreaObjects: DispatchAreaObject[] = [];
            for (const key of Object.keys(form)) {
                const value = form[key];
                if (value === 'MONITOR' || value === 'OWNER') {
                    dispatchAreaObjects.push({
                        ...allDispatchAreaObjectsByDispatchAreaId[key],
                        dispatchAreaUserType: value,
                    });
                }
            }
            return apiClient.request<ManageDispatchAreasResource.UpdateDispatchAreaOwnershipLinksForUser1>(
                {
                    method: 'POST',
                    url: 'manage_dispatch_areas/update',
                    data: {
                        dispatchAreaObjects,
                    },
                }
            );
        },
        {
            onSuccess() {
                close();
            },
        }
    );

    const userDispatchAreasByDispatchAreaId = React.useMemo(() => {
        return Object.fromEntries(
            userDispatchAreaObjects?.map((dispatchAreaObject) => [
                dispatchAreaObject.id,
                dispatchAreaObject.dispatchAreaUserType,
            ]) ?? []
        );
    }, [userDispatchAreaObjects]);

    React.useEffect(() => {
        if (manageDispatchAreasView) {
            form.reset(
                Object.fromEntries(
                    allDispatchAreaObjects?.map((dispatchAreaObject) => [
                        dispatchAreaObject.id.toString(),
                        userDispatchAreasByDispatchAreaId[dispatchAreaObject.id]
                            ? userDispatchAreasByDispatchAreaId[dispatchAreaObject.id.toString()] ??
                              'NONE'
                            : 'NONE',
                    ]) ?? []
                )
            );
        }
    }, [allDispatchAreaObjects, form, manageDispatchAreasView, userDispatchAreasByDispatchAreaId]);

    const onSubmit = React.useCallback(
        (props: DispatchAreaManagementForm): void => {
            mutation.mutate(props);
        },
        [mutation]
    );

    return (
        <IntegratedFormProvider query={query} mutation={mutation} form={form} onSubmit={onSubmit}>
            <BlankModal scrollBehavior="inside" size="xl" isOpen={true} onClose={close}>
                <ModalHeader>
                    <Flex direction="column">
                        <FormState>
                            {(formState) =>
                                formState.query.isInitialLoading ? (
                                    <Flex alignItems="center" justifyContent="center">
                                        <Spinner />
                                    </Flex>
                                ) : (
                                    <>
                                        <Box mb="12px">
                                            <Text>Manage D-Areas</Text>
                                        </Box>
                                        <DispatchFor
                                            form={form}
                                            allDispatchAreaObjects={allDispatchAreaObjects ?? []}
                                            allDispatchAreaGroupObjects={
                                                allDispatchAreaGroupObjects ?? []
                                            }
                                        />
                                    </>
                                )
                            }
                        </FormState>
                    </Flex>
                </ModalHeader>
                <ModalBody>
                    <FormBody>
                        <VStack alignItems="normal" maxH="580px">
                            <QuickGrabSection
                                dispatchAreaGroupObjects={allDispatchAreaGroupObjects}
                            />
                            <SelectionTables dispatchAreas={allDispatchAreaObjects} />
                        </VStack>
                    </FormBody>
                </ModalBody>
                <ModalFooter>
                    <HStack w="100%" justifyContent="flex-end" spacing={2}>
                        <FormCancelButton onClick={close} />
                        <FormSaveButton />
                    </HStack>
                </ModalFooter>
            </BlankModal>
        </IntegratedFormProvider>
    );
};
